<script lang="ts">
  import {
    BAND_LABELS,
    groupDigits,
    OpsError,
    parseOps,
    predict,
    type Band,
    type Prediction,
  } from '../../lib/complexity';
  import type { MeterDepth } from '../../lib/router';
  import { DEPTH_PRESETS, depthPreset } from './loop-slider';
  import { powToSpeech, zerosText } from './notation';
  import Pow from './Pow.svelte';
  import { DEFAULT_LIMIT_SEC, limitRatio } from './prediction';
  import StateIcon from './StateIcon.svelte';

  /**
   * 見積もり（layout-and-navigation.md §6）。制約 N の最大と重なり（または ops 式）から、
   * いちばん内側のループが回る回数と秒数を、実行せずに出す。
   */
  interface Props {
    constraintN?: number;
    depth?: MeterDepth;
    /** カードから開いたときの ops 式。あれば「式を書く」に入れる。 */
    ops?: string | undefined;
  }

  let { constraintN = $bindable(200_000), depth = $bindable(2), ops }: Props = $props();

  const uid = $props.id();
  // 初期値だけを受け取る（以後は入力欄が持つ）。
  const initial = (() => ({ n: constraintN, ops }))();
  let nText = $state(groupDigits(initial.n));
  let mode = $state<string>(initial.ops !== undefined ? 'custom' : String(depth));
  let customOps = $state(initial.ops ?? 'N*(N-1)/2');

  const parsedN = $derived.by((): number | null => {
    const digits = nText.replace(/[,\s_]/g, '');
    if (!/^\d+$/.test(digits)) return null;
    const v = Number(digits);
    return v >= 1 && v <= 1e18 ? v : null;
  });

  $effect(() => {
    if (parsedN !== null) constraintN = parsedN;
  });

  function onmode(e: Event) {
    const el = e.currentTarget;
    if (!(el instanceof HTMLSelectElement)) return;
    const v = el.value;
    mode = v;
    const preset = DEPTH_PRESETS.find((d) => String(d.id) === v);
    if (preset) depth = preset.id;
  }

  const expr = $derived(mode === 'custom' ? customOps : depthPreset(depth).ops);

  const result = $derived.by(
    (): { ok: true; p: Prediction } | { ok: false; message: string } | null => {
      if (parsedN === null) return null;
      try {
        const tree = parseOps(expr);
        const p = predict(tree, { N: parsedN });
        if (!Number.isFinite(p.ops) || p.ops < 0) {
          return { ok: false, message: '式の値を計算できませんでした。' };
        }
        return { ok: true, p };
      } catch (e) {
        if (e instanceof OpsError) {
          const at = e.position >= 0 ? `（${e.position + 1} 文字目）` : '';
          const unknown = /unknown identifier/.test(e.message);
          return {
            ok: false,
            message: unknown
              ? '式に使える変数は N だけです。'
              : `式を読み取れませんでした${at}。使える記号は + - * / ^ ( ) と log2(…)、sqrt(…) です。`,
          };
        }
        throw e;
      }
    },
  );

  const ratio = $derived(result?.ok ? limitRatio(result.p.ops, DEFAULT_LIMIT_SEC) : null);
  const bandIcon: Record<Band, 'ok' | 'warn' | 'over'> = {
    fits: 'ok',
    borderline: 'warn',
    exceeds: 'over',
  };
</script>

<div class="predict">
  <div class="inputs">
    <div class="field">
      <label for="{uid}-n">制約の N の最大値</label>
      <input
        id="{uid}-n"
        type="text"
        inputmode="numeric"
        class="num"
        bind:value={nText}
        aria-invalid={parsedN === null}
        aria-describedby={parsedN === null ? `${uid}-n-err` : undefined}
        onblur={() => {
          if (parsedN !== null) nText = groupDigits(parsedN);
        }}
      />
      {#if parsedN === null}
        <p id="{uid}-n-err" class="field-error">
          <StateIcon kind="warn" />N は 1 以上の整数で入力してください。
        </p>
      {/if}
    </div>
    <div class="field">
      <label for="{uid}-depth">ループの重なり方</label>
      <select id="{uid}-depth" value={mode} onchange={onmode} data-testid="meter-depth">
        {#each DEPTH_PRESETS as d (d.id)}
          <option value={String(d.id)}>{d.label}</option>
        {/each}
        <option value="custom">式を書く</option>
      </select>
    </div>
    {#if mode === 'custom'}
      <div class="field">
        <label for="{uid}-ops">回数の式（変数は N）</label>
        <input
          id="{uid}-ops"
          type="text"
          class="mono"
          spellcheck="false"
          autocomplete="off"
          bind:value={customOps}
        />
      </div>
    {/if}
    <p class="text-soft small">
      時間制限は AtCoder の多くの問題と同じ {DEFAULT_LIMIT_SEC} 秒として判定します。
    </p>
  </div>

  <div class="outputs" aria-live="polite" data-testid="meter-prediction">
    {#if result === null}
      <p class="text-soft small">N を入力すると回数を見積もります。</p>
    {:else if !result.ok}
      <p class="field-error"><StateIcon kind="warn" />{result.message}</p>
    {:else}
      <div class="text-soft small">いちばん内側のループが回る回数（式: <code>{expr}</code>）</div>
      <div class="count-big num" data-testid="predict-count">{groupDigits(result.p.ops)} 回</div>
      <div class="count-alt">
        <span class="sci num" aria-label={powToSpeech(result.p.countText)}
          ><Pow text={result.p.countText} /></span
        >（{zerosText(result.p.ops)}）
      </div>
      <div>
        1 秒に約 10<sup>8</sup> 回動くとすると、かかる時間は
        <strong class="num" data-testid="predict-seconds">{result.p.secondsText}</strong>
      </div>
      {#if ratio}
        <div
          class="ratio-meter"
          role="meter"
          aria-label="時間制限に対する割合"
          aria-valuemin="0"
          aria-valuemax="100"
          aria-valuenow={ratio.valueNow}
          aria-valuetext={ratio.text}
        >
          <span class="fill {ratio.state}" style:width="{ratio.fillPercent}%"></span>
          <span class="mark" aria-hidden="true"></span>
        </div>
        <div class="ratio-legend" aria-hidden="true">
          <span>0</span><span>制限 {DEFAULT_LIMIT_SEC} 秒</span><span>制限の 2 倍</span>
        </div>
        <p class="verdict-line {result.p.band}" data-testid="predict-band">
          <StateIcon kind={bandIcon[result.p.band]} />
          <span>{ratio.text}。{BAND_LABELS[result.p.band]}です。</span>
        </p>
      {/if}
    {/if}
  </div>
</div>

<style>
  .predict {
    display: grid;
    grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
    gap: var(--space-5);
    align-items: start;
  }
  @media (max-width: 1023px) {
    .predict {
      grid-template-columns: minmax(0, 1fr);
    }
  }
  .inputs,
  .outputs {
    display: flex;
    flex-direction: column;
    gap: var(--space-3);
  }
  .field label {
    display: block;
    font-size: var(--fs-sm);
    margin-bottom: var(--space-1);
  }
  .field input,
  .field select {
    width: 100%;
  }
  .mono {
    font-family: var(--font-mono);
  }
  .small {
    font-size: var(--fs-sm);
  }
  .field-error {
    display: flex;
    align-items: center;
    gap: var(--space-2);
    color: var(--warn-text);
    font-size: var(--fs-sm);
    margin-top: var(--space-1);
  }
  .count-big {
    font-size: var(--fs-2xl);
    font-weight: var(--fw-heavy);
    line-height: 1.15;
    overflow-wrap: anywhere;
  }
  .count-alt {
    color: var(--text-soft);
    font-size: var(--fs-sm);
  }
  .count-alt .sci {
    color: var(--text);
    font-weight: var(--fw-semibold);
  }
  .ratio-meter {
    position: relative;
    height: 14px;
    border-radius: var(--radius-pill);
    background: var(--meter-track);
    overflow: hidden;
  }
  .ratio-meter .fill {
    display: block;
    height: 100%;
    border-radius: inherit;
    transition: width var(--dur-base);
  }
  .fill.ok {
    background: var(--meter-ok);
  }
  .fill.warn {
    background: var(--meter-warn);
  }
  .fill.over {
    background: var(--meter-over);
  }
  .ratio-meter .mark {
    position: absolute;
    top: 0;
    bottom: 0;
    left: calc(50% - 1px);
    width: 2px;
    background: var(--text);
  }
  .ratio-legend {
    display: flex;
    justify-content: space-between;
    font-size: var(--fs-xs);
    color: var(--text-soft);
    font-variant-numeric: tabular-nums;
  }
  .verdict-line {
    display: flex;
    align-items: center;
    gap: var(--space-2);
    font-weight: var(--fw-semibold);
  }
  .verdict-line.fits {
    color: var(--ok-text);
  }
  .verdict-line.borderline {
    color: var(--warn-text);
  }
  .verdict-line.exceeds {
    color: var(--err-text);
  }
</style>
